import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarCheck, Inbox, Map, TrendingUp, Globe } from "lucide-react";
import { useSiteData } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export const DashboardTab = () => {
  const { fmtPrice } = useSiteData();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/dashboard`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) {
    return <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" data-testid="dashboard-loading-spinner" />;
  }

  const stats = [
    { icon: CalendarCheck, label: "Tour bookings", value: data.total_bookings, testid: "stat-bookings" },
    { icon: Inbox, label: "New inquiries", value: data.new_inquiries, testid: "stat-inquiries" },
    { icon: Map, label: "Published tours", value: `${data.published_tours}/${data.total_tours}`, testid: "stat-tours" },
    { icon: TrendingUp, label: "Est. booking value", value: fmtPrice(data.revenue_estimate), testid: "stat-revenue" },
    { icon: Globe, label: "Destinations", value: data.total_destinations, testid: "stat-destinations" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard-tab">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => (
          <div key={s.testid} data-testid={s.testid} className="rounded-3xl bg-white border border-slate-100 p-5 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
            <span className="w-10 h-10 rounded-2xl bg-[var(--brand-soft)] flex items-center justify-center">
              <s.icon className="w-5 h-5 text-[var(--brand)]" />
            </span>
            <p className="mt-3 font-display font-extrabold text-2xl text-[#0b132b]">{s.value}</p>
            <p className="text-xs font-semibold text-slate-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]" data-testid="dashboard-recent">
          <h3 className="font-display font-bold text-lg text-[#0b132b] mb-4">Recent inquiries</h3>
          {data.recent.length === 0 ? (
            <p className="text-sm text-slate-400">No inquiries yet.</p>
          ) : (
            <div className="space-y-3">
              {data.recent.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 border-b border-slate-50 last:border-0 pb-3 last:pb-0">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#0b132b] truncate">{e.name}</p>
                    <p className="text-xs text-slate-400 truncate">{e.tour || e.car || e.message || "General enquiry"}</p>
                  </div>
                  <span className="text-[11px] font-semibold text-slate-400 shrink-0">{fmt(e.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]" data-testid="dashboard-upcoming">
          <h3 className="font-display font-bold text-lg text-[#0b132b] mb-4">Upcoming tours</h3>
          {data.upcoming.length === 0 ? (
            <p className="text-sm text-slate-400">No upcoming tour dates booked.</p>
          ) : (
            <div className="space-y-3">
              {data.upcoming.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 border-b border-slate-50 last:border-0 pb-3 last:pb-0">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#0b132b] truncate">{e.tour || "Tour"}</p>
                    <p className="text-xs text-slate-400 truncate">{e.name}{e.guests ? ` · ${e.guests} guests` : ""}</p>
                  </div>
                  <span className="rounded-full bg-[var(--brand-soft)] px-3 py-1 text-[11px] font-bold text-[var(--brand)] shrink-0">{e.pickup_date}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
